import React, { useEffect, useMemo, useState } from "react";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import {
    filterData,
    getDefaultTablePreferences,
    getTableHeaders,
    handleAuditRecordsFormatting,
    handleReload,
    handleSort,
    handleTableDataFormatting,
} from "./utils";
import { Space } from "../space/Space";
import _, { uniqueId } from "lodash";

import { useFilterSheets } from "../../features/filter-sheets/hooks";
import { FilterTag } from "../../features/filter-sheets/FilterTag";
import { TableFooter } from "./TableFooter";
import { TableSortDirection } from "@amzn/meridian/table/table";
import { ITableProps, tableMapping } from "./definitions";
import { TableHeader } from "./TableHeader";
import { useTable } from "./hooks";
import { TableRows } from "./TableRows";
import { filtersByMailType } from "../../features/filter-sheets/utils";
import { useSheets } from "../../features/sheets/hooks";
import { EmptyState } from "./EmptyState";
import Column from "@amzn/meridian/column";
import Loader from "@amzn/meridian/loader";
import { DEFAULT_LIMIT_PER_REQUEST, DEFAULT_PAGE_SIZE } from "../../constants/defaultsConstants";
import { parseJSON } from "../../utils/helperUtils";
import { useLazyGetPropertyDetailsQuery } from "../../endpoints/getArtifacts";
import { useEditingDocument } from "../edit-document-modal/hooks";
import { TablePreferencesModal } from "./TablePreferencesModal";
import { useLocalStorage } from "../../utils/local-storage";
import { useLazyGetAuditRecordsQuery } from "../../endpoints/auditEventTrigger";

export const TableComponent: React.FC<ITableProps> = ({
    tableQueryParams,
    tableUseCase,
    tableName,
    onSearch,
    showDividers,
}) => {
    const [sortColumn, setSortColumn] = useState();
    const [tableData, setTableData] = useState([]);
    const [resultData, setResultData] = useState([]);
    const [sortDirection, setSortDirection] = useState<TableSortDirection>("ascending");
    const [tableSearchInput, setTableSearchInput] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [requestNumber, setRequestNumber] = useState<number>(1);
    const [request, setRequest] = useState(null);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const { currentTable, currenTableId } = useTable();
    const [tableType, setTableType] = useState(currentTable);

    const [preferences, setPreferences] = useLocalStorage(
        "table-preferences",
        getDefaultTablePreferences()
    );
    const [openPreferenceModal, setOpenPreferenceModal] = useState(false);

    const { updateSheets } = useSheets();
    const [
        properties,
        { data: propertiesData, isFetching: isFetchingProperties, isSuccess: isPropertiesSuccess },
    ] = useLazyGetPropertyDetailsQuery();
    const [
        triggerAuditRecords,
        { data: auditRecordsData, isFetching: isFetchingAuditRecords, isSuccess: isAuditSuccess },
    ] = useLazyGetAuditRecordsQuery();
    const [totalCount, setTotalCount] = React.useState<number>(
        propertiesData?.data?.totalCount ||
            auditRecordsData?.auditItemsResponse?.totalItemsInResponse ||
            0
    );

    const addExtraPage =
        Math.ceil(resultData.length / Number(pageSize)) < Math.ceil(totalCount / Number(pageSize));

    const numberOfPages = Math.ceil(resultData.length / Number(pageSize) + (addExtraPage ? 1 : 0));
    const filterPageCount = Math.ceil(tableData.length / Number(pageSize));
    const { filterMap } = useFilterSheets();
    const firstVisibleIndex = Math.floor((currentPage - 1) * Number(pageSize));
    const lastVisibleIndex = Math.floor(firstVisibleIndex + Number(pageSize));
    const { isEditSheetOpen } = useEditingDocument();
    const onSort = ({ sortColumn, sortDirection }) => {
        setTableData(handleSort(tableData, sortDirection, sortColumn));
        setSortDirection(sortDirection);
        setSortColumn(sortColumn);
    };

    useEffect(() => {
        tableName ? setTableType(tableName) : setTableType(currentTable);
    }, [currentTable, tableName]);

    useEffect(() => {
        setTableData(resultData);
    }, [resultData]);

    useEffect(() => {
        setTableData(filterData(filterMap, resultData));
        setCurrentPage(1);
    }, [filterMap, resultData]);

    useEffect(() => {
        if (request) {
            tableName == "audit-records-mails" || tableName == "audit-records-data-source-files"
                ? triggerAuditRecords(request)
                : properties(request);
        }
    }, [request]);

    useEffect(() => {
        tableName == "audit-records-mails" || tableName == "audit-records-data-source-files"
            ? handleLoadMoreAuditRecords(currentPage)
            : handleLoadMoreProperties(currentPage);
    }, [currentPage]);

    useEffect(() => {
        setCurrentPage(1);
        setRequestNumber(1);
    }, [pageSize]);

    const loadedProperties = useMemo(() => {
        if (requestNumber === 1) {
            return (
                handleTableDataFormatting(parseJSON(propertiesData ?? [])?.data?.dataList ?? []) ??
                []
            );
        }

        return resultData.concat(
            handleTableDataFormatting(parseJSON(propertiesData).data.dataList) ?? []
        );
    }, [propertiesData?.data?.dataList]);

    const loadedAuditRecords = useMemo(() => {
        if (requestNumber === 1) {
            return (
                handleAuditRecordsFormatting(
                    parseJSON(auditRecordsData ?? [])?.auditItemsResponse?.auditItemsList ?? []
                ) ?? []
            );
        }

        return resultData.concat(
            handleAuditRecordsFormatting(
                parseJSON(auditRecordsData ?? {})?.auditItemsResponse?.auditItemsList ?? []
            ) ?? []
        );
    }, [auditRecordsData?.auditItemsResponse?.auditItemsList]);

    const handleLoadMoreProperties = currentPageIndex => {
        if (currentPageIndex === numberOfPages) {
            if (resultData.length < totalCount) {
                setRequest({
                    useCase: tableUseCase || "GET_DOCUMENTS",
                    businessUseCase: "SMART",
                    queryParams: {
                        limitPerRequest: DEFAULT_LIMIT_PER_REQUEST,
                        requestNumber: requestNumber + 1,
                        ...tableQueryParams,
                    },
                });
                setRequestNumber(requestNumber + 1);
            }
        }
    };

    const handleLoadMoreAuditRecords = currentPageIndex => {
        if (currentPageIndex === numberOfPages) {
            if (auditRecordsData?.exclusiveStartKeyForNextCall != null) {
                setRequest({
                    eventUseCase: "FILE_UPLOAD",
                    businessUseCase: "SMART",
                    exclusiveStartKey: auditRecordsData?.exclusiveStartKeyForNextCall,
                });
                setRequestNumber(requestNumber + 1);
            }
        }
    };

    useEffect(() => {
        if (!_.isEmpty(loadedProperties)) {
            setTotalCount(propertiesData?.data?.totalCount ?? 0);
            setResultData(loadedProperties);
        } else {
            setTotalCount(
                totalCount + (auditRecordsData?.auditItemsResponse?.totalItemsInResponse ?? 0)
            );
            setResultData(loadedAuditRecords);
        }
    }, [loadedProperties, loadedAuditRecords, tableType]);

    useEffect(() => {
        setCurrentPage(1);
        setRequestNumber(1);
    }, [tableType]);

    useEffect(() => {
        updateSheets(filtersByMailType[tableType]);
        if (tableName === "audit-records-mails" || tableName == "audit-records-data-source-files") {
            setRequest({
                ...tableQueryParams,
            });
        } else {
            setRequest({
                useCase: tableUseCase || "GET_DOCUMENTS",
                businessUseCase: "SMART",
                queryParams: {
                    limitPerRequest: DEFAULT_LIMIT_PER_REQUEST,
                    requestNumber: requestNumber,
                    ...tableQueryParams,
                },
            });
        }
    }, [tableQueryParams, tableUseCase]);

    useEffect(() => {
        if (!isEditSheetOpen && request && !onSearch) {
            properties(request);
        }
    }, [isEditSheetOpen]);

    useEffect(() => {
        setRequestNumber(1);
        handleReload(triggerAuditRecords, request); //TODO:
    }, [currenTableId]);

    return (
        <Column>
            <TableHeader
                setTableSearchInput={setTableSearchInput}
                tableSearchInput={tableSearchInput}
                tableData={tableData}
                currentTable={tableType}
                setOpenPreferenceModal={setOpenPreferenceModal}
            />
            <Column overflowX={"scroll"}>
                <Table
                    headerRows={1}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={onSort}
                    showDividers={showDividers || preferences.showDividers}
                    spacing={_.defaultTo(preferences.tableSpacing, "small")}
                    showStripes={preferences.showStripes}
                >
                    <TablePreferencesModal
                        openPreferenceModal={openPreferenceModal}
                        setOpenPreferenceModal={setOpenPreferenceModal}
                        preferences={preferences}
                        setPreferences={setPreferences}
                        tableName={tableType}
                    />
                    <TableRow highlightOnHover={true}>
                        {getTableHeaders(preferences, tableType)
                            .sort((a, b) => a?.order - b?.order)
                            .map(cell => {
                                return (
                                    <TableCell sortColumn={cell.sortValue ?? null}>
                                        {cell.label}
                                    </TableCell>
                                );
                            })}
                    </TableRow>

                    {filterMap.size > 0 ? (
                        <>
                            <Text type={"h100"}> Filter result ({tableData.length})</Text>
                            <Space size={"xs"} />
                        </>
                    ) : null}
                    <Row wrap={"down"}>
                        {[...filterMap.entries()].map(([key, { value }]) => {
                            return <FilterTag filterKey={key} filterValue={value} />;
                        })}
                    </Row>

                    {tableData?.slice(firstVisibleIndex, lastVisibleIndex).map((row, index) => {
                        return (
                            <TableRow
                                key={uniqueId(row.mailId)}
                                highlightOnHover={preferences.highlightOnHover}
                            >
                                <TableRows
                                    data={tableData}
                                    currentRow={Number(firstVisibleIndex + index)}
                                    currentTable={tableType}
                                    headers={getTableHeaders(preferences, tableType)}
                                />
                            </TableRow>
                        );
                    })}
                    {(isFetchingProperties || isFetchingAuditRecords) && (
                        <Column width={"100%"}>
                            <Loader type="linear" />
                        </Column>
                    )}
                </Table>
            </Column>
            {tableData?.length > 0 ? (
                <>
                    <Space size={"xxs"} />
                    <TableFooter
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        numberOfPages={filterMap.size > 0 ? filterPageCount : numberOfPages}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                    />
                </>
            ) : (!isFetchingProperties || !isFetchingAuditRecords) &&
              (isPropertiesSuccess || isAuditSuccess) ? (
                <EmptyState message={tableMapping[tableType]?.emptyMessage} />
            ) : null}
        </Column>
    );
};
